import React, { useState, useRef } from "react";
import { X, Play, Pause, IndianRupee } from "lucide-react";
import Demovideo from "../../assets/DemoVideo.mp4";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const SignupSuccessModal = ({ isOpen, setIsOpen, email, password }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const { login, loading, checkReadOnlyMode } = useAuth();

  const handleStartFreeTrial = async () => {
    try {
      const result = await login(email, password);
    } catch (error) {
      toast.error("Something went wrong during login.");
      console.error("Login error:", error);
    }
  };

  const handleScheduleDemo = () => {
    window.open("https://calendly.com/rishabgupta1703", "_blank");
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={24} />
        </button>

        <div className="flex flex-col lg:flex-row">
          <div className="flex-1 p-8 lg:p-12">
            <div className="mb-8 flex items-center justify-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-purple-600"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 flex items-center">
              <IndianRupee style={{ fontSize: "50px", fontWeight: 800 }} /> 0
              for <span className="text-purple-600"> &nbsp;30 days</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8">
              Start booking your demo today with the NABS team
            </p>

            {/* Features list */}
            <div className="space-y-4 mb-10">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-purple-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
                  </svg>
                </div>
                <span className="text-gray-700">Automated Notice Fetching</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-purple-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
                  </svg>
                </div>
                <span className="text-gray-700">Free Response Generation</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-purple-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
                  </svg>
                </div>
                <span className="text-gray-700">Free Clients Addition</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-purple-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z" />
                  </svg>
                </div>
                <span className="text-gray-700">
                  AI-Powered Response Drafting
                </span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleStartFreeTrial}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-8 rounded-xl transition-colors text-lg"
            >
              Start Free Trial
            </button>
          </div>

          <div className="flex-1 bg-gradient-to-br from-purple-100 via-blue-50 to-orange-100 p-8 lg:p-12 flex flex-col justify-center">
            <div className="relative mb-8">
              <div className="bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
                <div
                  className="aspect-video bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center relative cursor-pointer"
                  onClick={handleVideoClick}
                >
                  <video
                    ref={videoRef}
                    src={Demovideo}
                    className="absolute inset-0 w-full h-full object-cover"
                    onEnded={handleVideoEnded}
                  />
                  {!isPlaying && (
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-opacity-30 transition-all">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                  )}
                  {isPlaying && (
                    <div className="absolute top-4 right-4 w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center backdrop-blur-sm opacity-0 hover:opacity-100 transition-opacity">
                      <Pause className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                Book a call with our team
              </h3>
              <p className="text-gray-600 mb-6">
                Want to learn more? Schedule a call with our team for a demo.
              </p>

              <button
                onClick={handleScheduleDemo}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-8 rounded-xl border border-gray-200 transition-colors"
              >
                Schedule a demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupSuccessModal;
